<template>
  <span>
    <v-tooltip bottom>
      <template v-slot:activator="{ on }">
        <v-btn icon v-on="on" @click="open">
          <v-icon>{{ isDebito ? "mdi-cash-multiple" : "mdi-file-percent-outline" }}</v-icon>
        </v-btn>
      </template>
      <span>{{ isDebito ? "Pagamenti e crediti applicati" : "Erosione credito" }}</span>
    </v-tooltip>

    <v-dialog v-model="dialog" max-width="700">
      <v-card>
        <v-card-title class="grey--text text-subtitle-1">
          {{ isDebito ? "Copertura debito" : "Erosione credito" }}
        </v-card-title>
        <v-card-text>
          <v-alert v-if="errorMsg" type="error" dense text>{{ errorMsg }}</v-alert>
          <v-alert v-if="warningMsg" type="warning" dense text>{{ warningMsg }}</v-alert>

          <v-row v-if="loading" justify="center" class="my-6">
            <v-progress-circular indeterminate color="primary" />
          </v-row>

          <template v-else>
            <v-row v-if="isDebito" no-gutters dense class="mb-3">
              <v-col cols="6" class="text-caption grey--text">
                {{ clienteNome }} — {{ formatTs(contabile.ts) }}
              </v-col>
              <v-col cols="6" class="text-caption text-right">
                <span :class="contabile.verde ? 'green--text' : 'blue--text'">{{
                  contabile.verde ? "Verde" : "Blu"
                }}</span>
                <span v-if="numeroOrdine"> — Ordine {{ numeroOrdine }}</span>
                <span v-if="contabile.ddt"> — DDT {{ contabile.ddt }}</span>
              </v-col>
            </v-row>
            <v-row dense>
              <v-col cols="6">
                <div class="grey--text text-caption">
                  {{ isDebito ? "Importo debito" : "Imponibile fattura" }}
                </div>
                <div class="text-h6">
                  € {{ (isDebito ? importoDebito : importoBase || 0).toFixed(2) }}
                </div>
              </v-col>
              <v-col cols="6">
                <div class="grey--text text-caption">Residuo</div>
                <div
                  class="text-h6"
                  :class="(isDebito ? residuoDebito : residuo || 0) > 0 ? 'orange--text' : 'green--text'"
                >
                  € {{ (isDebito ? residuoDebito : residuo || 0).toFixed(2) }}
                </div>
              </v-col>
            </v-row>
            <v-row v-if="!isDebito && (contabile.tipo === 2 || contabile.tipo === 3)" no-gutters class="mt-1">
              <v-col class="grey--text text-caption">
                Metodo di pagamento: {{ nomeMetodo(contabile.metodo_pagamento) || "non indicato" }}
                <span v-if="contabile.tipo === 3">
                  — fattura {{ contabile.saldato ? "saldata" : "non ancora saldata" }}{{
                    contabile.saldato && contabile.ts_saldo
                      ? " il " + formatTs(contabile.ts_saldo)
                      : ""
                  }}
                </span>
              </v-col>
            </v-row>
            <v-row v-if="!isDebito && (residuo || 0) > 0" no-gutters class="mt-2">
              <v-col cols="12">
                <v-btn x-small outlined color="orange darken-2" @click="dialogEstingui = true">
                  <v-icon left x-small>mdi-close-circle-outline</v-icon>
                  Estingui credito residuo
                </v-btn>
              </v-col>
            </v-row>

            <template v-if="isFatturaARate">
              <v-divider class="my-4" />
              <div class="text-subtitle-2 mb-2">
                Pagamento a rate
                <span class="text-caption grey--text font-weight-regular">
                  (imponibile + IVA, € {{ (importoTotalePagamento || 0).toFixed(2) }})
                </span>
              </div>
              <v-row dense class="mb-2">
                <v-col cols="4">
                  <div class="grey--text text-caption">Incassato</div>
                  <div class="text-h6 green--text">
                    € {{ (incassatoPagamento || 0).toFixed(2) }}
                  </div>
                </v-col>
                <v-col cols="4">
                  <div class="grey--text text-caption">Da assegnare a una rata</div>
                  <div
                    class="text-h6"
                    :class="(residuoPagamento || 0) > 0 ? 'orange--text' : 'green--text'"
                  >
                    € {{ (residuoPagamento || 0).toFixed(2) }}
                  </div>
                </v-col>
              </v-row>
              <template v-if="(residuoPagamento || 0) > 0">
                <v-row dense align="center">
                  <v-col cols="3">
                    <v-text-field
                      v-model.number="numeroRatePianificate"
                      label="Numero rate"
                      type="number"
                      min="1"
                      outlined
                      dense
                      hide-details
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-menu
                      v-model="menuScadenzaIniziale"
                      :close-on-content-click="false"
                      offset-y
                    >
                      <template v-slot:activator="{ on }">
                        <v-text-field
                          v-model="scadenzaInizialeFormatted"
                          label="Prima scadenza (opz.)"
                          prepend-inner-icon="mdi-calendar"
                          readonly
                          clearable
                          outlined
                          dense
                          hide-details
                          v-on="on"
                          @click:clear="scadenzaIniziale = null"
                        />
                      </template>
                      <v-date-picker
                        v-model="scadenzaIniziale"
                        no-title
                        @input="menuScadenzaIniziale = false"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="3">
                    <v-text-field
                      v-model.number="intervalloGiorni"
                      label="Ogni (giorni)"
                      type="number"
                      min="1"
                      :disabled="!scadenzaIniziale"
                      outlined
                      dense
                      hide-details
                    />
                  </v-col>
                </v-row>
                <v-row dense class="mt-2">
                  <v-col cols="12">
                    <v-btn
                      color="primary"
                      outlined
                      block
                      :loading="loadingSuddividi"
                      @click="suddividiInRate"
                      >{{
                        numeroRatePianificate === 1
                          ? "Crea 1 rata"
                          : `Suddividi in ${numeroRatePianificate || "X"} rate`
                      }}</v-btn
                    >
                  </v-col>
                </v-row>
              </template>
              <v-alert v-else type="success" dense text class="mt-2">
                Fattura interamente assegnata a rate.
              </v-alert>

              <div class="text-subtitle-2 mb-2 mt-4">Rate</div>
              <v-row v-if="comeDebito.length === 0" no-gutters>
                <v-col class="grey--text">Nessuna rata ancora registrata</v-col>
              </v-row>
              <template v-else>
                <v-row no-gutters class="text-caption grey--text">
                  <v-col cols="3">Rata / metodo</v-col>
                  <v-col cols="2">Importo</v-col>
                  <v-col cols="2">Scadenza</v-col>
                  <v-col cols="2">Incasso</v-col>
                  <v-col cols="3"></v-col>
                </v-row>
                <v-row v-for="a in comeDebito" :key="a.id" no-gutters align="center" class="py-1">
                  <v-col cols="3">
                    <span v-if="a.saldato">{{ nomeMetodo(a.metodo_pagamento) || "Pagamento" }}</span>
                    <span v-else class="orange--text">Rata pianificata</span>
                    <div v-if="a.note" class="text-caption grey--text text-truncate">{{ a.note }}</div>
                  </v-col>
                  <v-col cols="2">€ {{ a.importo.toFixed(2) }}</v-col>
                  <v-col cols="2">
                    <span v-if="a.saldato" class="text-caption grey--text">
                      {{ formatScadenza(a.scadenza) || "-" }}
                    </span>
                    <v-menu
                      v-else
                      :value="menuScadenzaRataAperto === a.id"
                      :close-on-content-click="false"
                      offset-y
                      @input="(v) => (menuScadenzaRataAperto = v ? a.id : null)"
                    >
                      <template v-slot:activator="{ on }">
                        <span
                          class="text-caption text-decoration-underline"
                          style="cursor: pointer"
                          :class="coloreScadenza(a) || 'grey--text'"
                          v-on="on"
                        >
                          {{ formatScadenza(a.scadenza) || "scadenza" }}
                        </span>
                      </template>
                      <v-date-picker
                        :value="scadenzaISO(a.scadenza)"
                        :loading="loadingScadenzaRata === a.id"
                        no-title
                        @input="(v) => modificaScadenza(a, v)"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="2">
                    <v-menu
                      v-if="a.saldato"
                      :value="menuTsSaldoRataAperto === a.id"
                      :close-on-content-click="false"
                      offset-y
                      @input="(v) => (menuTsSaldoRataAperto = v ? a.id : null)"
                    >
                      <template v-slot:activator="{ on }">
                        <span
                          class="text-caption text-decoration-underline"
                          style="cursor: pointer"
                          v-on="on"
                        >
                          {{ formatTs(a.ts_saldo) }}
                        </span>
                      </template>
                      <v-date-picker
                        :value="scadenzaISO(a.ts_saldo)"
                        :loading="loadingTsSaldoRata === a.id"
                        no-title
                        @input="(v) => modificaTsSaldo(a, v)"
                      />
                    </v-menu>
                    <span v-else class="text-caption grey--text">-</span>
                  </v-col>
                  <v-col cols="3" class="text-right d-flex align-center justify-end">
                    <v-menu v-if="!a.saldato" :close-on-content-click="false" offset-y>
                      <template v-slot:activator="{ on }">
                        <v-checkbox
                          :input-value="false"
                          :loading="loadingSaldatoRata === a.id"
                          readonly
                          dense
                          hide-details
                          class="mt-0 pt-0"
                          v-on="on"
                        ></v-checkbox>
                      </template>
                      <v-list dense>
                        <v-subheader>Segna come pagata con...</v-subheader>
                        <v-list-item
                          v-for="m in metodiPagamento"
                          :key="m.value"
                          @click="segnaRataSaldata(a, true, m.value)"
                        >
                          <v-list-item-title>{{ m.nome }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <v-checkbox
                      v-else
                      :input-value="true"
                      :loading="loadingSaldatoRata === a.id"
                      readonly
                      dense
                      hide-details
                      class="mt-0 pt-0"
                      @click="segnaRataSaldata(a, false)"
                    ></v-checkbox>
                    <v-btn icon x-small :loading="loadingElimina === a.id" @click="richiediEliminaRata(a)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </template>
              <v-dialog v-model="dialogConfermaEliminaRata" max-width="400">
                <v-card>
                  <v-card-title class="headline">Elimina rata</v-card-title>
                  <v-card-text>
                    Eliminare questa rata{{
                      rataDaEliminare ? " da € " + rataDaEliminare.importo.toFixed(2) : ""
                    }}? L'operazione non è reversibile.
                  </v-card-text>
                  <v-card-actions>
                    <v-spacer />
                    <v-btn color="grey" text @click="dialogConfermaEliminaRata = false">Annulla</v-btn>
                    <v-btn color="red" outlined @click="confermaEliminaRata">Elimina</v-btn>
                  </v-card-actions>
                </v-card>
              </v-dialog>
            </template>

            <v-divider class="my-4" />

            <template v-if="isDebito && residuoDebito > 0">
              <div class="text-subtitle-2 mb-2">Registra copertura</div>
              <v-row v-if="erosioneDisponibile" dense>
                <v-col cols="6">
                  <v-select
                    v-model="fonte"
                    :items="fonteOptions"
                    item-text="text"
                    item-value="value"
                    label="Fonte"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="4">
                  <v-text-field
                    v-model.number="importoDaAllocare"
                    label="Importo"
                    prefix="€"
                    type="number"
                    outlined
                    dense
                    hide-details
                    @focus="$event.target.select()"
                  />
                </v-col>
                <v-col cols="8" v-if="fonte === 'pagamento'">
                  <v-select
                    v-model="metodoPagamento"
                    :items="metodiPagamento"
                    item-text="nome"
                    item-value="value"
                    label="Metodo di pagamento"
                    outlined
                    dense
                    hide-details
                  />
                </v-col>
                <v-col cols="8" v-else>
                  <v-select
                    v-model="idCreditoSelezionato"
                    :items="creditiDisponibiliCoerenti"
                    item-value="id"
                    :item-text="testoCredito"
                    label="Credito da erodere"
                    outlined
                    dense
                    hide-details
                  >
                    <template v-slot:item="{ item }">
                      <span>
                        {{ item.tipo === 3 ? "Fattura di copertura" : "Pagamento Cliente" }}
                        — {{ formatTs(item.ts) }}{{ item.ddt ? " — DDT " + item.ddt : "" }}
                        — residuo € {{ item.residuo.toFixed(2) }}
                      </span>
                    </template>
                    <template v-slot:selection="{ item }">
                      <span>
                        {{ item.tipo === 3 ? "Fattura di copertura" : "Pagamento Cliente" }}
                        — residuo € {{ item.residuo.toFixed(2) }}
                      </span>
                    </template>
                  </v-select>
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="4">
                  <v-menu
                    v-model="menuDataSaldo"
                    :close-on-content-click="false"
                    offset-y
                  >
                    <template v-slot:activator="{ on }">
                      <v-text-field
                        v-model="dataSaldoFormatted"
                        label="Data"
                        prepend-inner-icon="mdi-calendar"
                        readonly
                        clearable
                        outlined
                        dense
                        hide-details
                        v-on="on"
                        @click:clear="dataSaldo = null"
                      />
                    </template>
                    <v-date-picker
                      v-model="dataSaldo"
                      no-title
                      @input="menuDataSaldo = false"
                    />
                  </v-menu>
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="12">
                  <v-text-field v-model="note" label="Note" outlined dense hide-details />
                </v-col>
              </v-row>
              <v-row dense class="mt-2">
                <v-col cols="12" class="text-right">
                  <v-btn
                    color="primary"
                    outlined
                    :loading="loadingSalva"
                    :disabled="fonte === 'credito' && !idCreditoSelezionato"
                    @click="registraAllocazione"
                    >Registra</v-btn
                  >
                </v-col>
              </v-row>
              <v-divider class="my-4" />
            </template>
            <template v-else-if="isDebito">
              <v-alert type="success" dense text>
                Debito interamente coperto: nessun'altra copertura è ammessa.
              </v-alert>
              <v-divider class="my-4" />
            </template>

            <div class="text-subtitle-2 mb-2">
              {{ isDebito ? "Coperture registrate" : "Debiti coperti da questo credito" }}
            </div>
            <template v-if="isDebito">
              <v-row v-if="comeDebito.length === 0" no-gutters>
                <v-col class="grey--text">Nessuna copertura registrata</v-col>
              </v-row>
              <template v-else>
                <v-row no-gutters class="text-caption grey--text">
                  <v-col cols="3">Data</v-col>
                  <v-col cols="4">Metodo / Fonte</v-col>
                  <v-col cols="3">Importo</v-col>
                  <v-col cols="2"></v-col>
                </v-row>
                <v-row v-for="a in comeDebito" :key="a.id" no-gutters align="center" class="py-1">
                  <v-col cols="3">
                    <v-menu
                      :value="menuTsCoperturaAperto === a.id"
                      :close-on-content-click="false"
                      offset-y
                      @input="(v) => (menuTsCoperturaAperto = v ? a.id : null)"
                    >
                      <template v-slot:activator="{ on }">
                        <span
                          class="text-decoration-underline"
                          style="cursor: pointer"
                          v-on="on"
                        >
                          {{ formatTs(a.ts) }}
                        </span>
                      </template>
                      <v-date-picker
                        :value="scadenzaISO(a.ts)"
                        :loading="loadingTsCopertura === a.id"
                        no-title
                        @input="(v) => modificaTsCopertura(a, v)"
                      />
                    </v-menu>
                  </v-col>
                  <v-col cols="4">
                    <span v-if="a.pagamento_origine">
                      {{ a.pagamento_origine.tipo === 3 ? "Fattura di copertura" : "Pagamento Cliente" }}
                      <span v-if="a.pagamento_origine.ddt">DDT {{ a.pagamento_origine.ddt }}</span>
                    </span>
                    <v-menu v-else offset-y>
                      <template v-slot:activator="{ on }">
                        <span
                          class="text-decoration-underline"
                          style="cursor: pointer"
                          v-on="on"
                        >
                          {{ nomeMetodo(a.metodo_pagamento) || "Pagamento" }}
                        </span>
                      </template>
                      <v-list dense>
                        <v-subheader>Metodo di pagamento</v-subheader>
                        <v-list-item
                          v-for="m in metodiPagamento"
                          :key="m.value"
                          @click="modificaMetodoCopertura(a, m.value)"
                        >
                          <v-list-item-title>{{ m.nome }}</v-list-item-title>
                        </v-list-item>
                      </v-list>
                    </v-menu>
                    <div
                      v-if="fonteAllocazione(a) && fonteAllocazione(a).tipo === 3"
                      class="text-caption d-flex align-center"
                      :class="fonteAllocazione(a).saldato ? 'green--text' : 'purple--text'"
                    >
                      <v-icon v-if="!fonteAllocazione(a).saldato" x-small color="purple darken-1" class="mr-1"
                        >mdi-timer-sand</v-icon
                      >
                      <template v-if="fonteAllocazione(a).saldato">
                        Fattura saldata{{
                          fonteAllocazione(a).ts_saldo
                            ? " il " + formatTs(fonteAllocazione(a).ts_saldo)
                            : ""
                        }}
                      </template>
                      <template v-else>Fattura non ancora saldata</template>
                    </div>
                    <div v-else-if="a.pagamento_origine" class="text-caption grey--text">
                      Da Pagamento Cliente
                    </div>
                    <div v-if="a.note" class="text-caption grey--text text-truncate">{{ a.note }}</div>
                  </v-col>
                  <v-col cols="3">€ {{ a.importo.toFixed(2) }}</v-col>
                  <v-col cols="2" class="text-right">
                    <v-btn icon x-small :loading="loadingElimina === a.id" @click="eliminaAllocazione(a.id)">
                      <v-icon small>mdi-delete</v-icon>
                    </v-btn>
                  </v-col>
                </v-row>
              </template>
            </template>
            <template v-else>
              <v-row v-if="comeCredito.length === 0" no-gutters>
                <v-col class="grey--text">Nessun debito ancora coperto da questo credito</v-col>
              </v-row>
              <v-row v-for="a in comeCredito" :key="a.id" no-gutters align="center" class="py-1">
                <v-col cols="4">{{ formatTs(a.ts) }}</v-col>
                <v-col cols="5">
                  <div v-if="a.debito && (a.debito.id_ordine || a.debito.ddt)">
                    <span v-if="a.debito.id_ordine">Ordine</span>
                    <ContabileOrdineRef
                      v-if="a.debito.id_ordine"
                      readonly
                      :id_ordine="a.debito.id_ordine"
                    />
                    <span v-if="a.debito.ddt" class="ml-1">DDT {{ a.debito.ddt }}</span>
                  </div>
                  <div v-else class="grey--text">Debito</div>
                  <div v-if="a.debito && a.debito.note" class="text-caption grey--text text-truncate">
                    {{ a.debito.note }}
                  </div>
                  <div v-if="a.note" class="text-caption grey--text text-truncate">{{ a.note }}</div>
                </v-col>
                <v-col cols="3">€ {{ a.importo.toFixed(2) }}</v-col>
              </v-row>
            </template>
          </template>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text color="grey" @click="dialog = false">Chiudi</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogEstingui" max-width="420">
      <v-card>
        <v-card-title class="headline">Estingui credito residuo</v-card-title>
        <v-card-text>
          Il residuo non ancora eroso (€ {{ (residuo || 0).toFixed(2) }}) verrà azzerato e non sarà
          più disponibile per erosioni future. L'importo già eroso non viene toccato. L'operazione
          non è reversibile automaticamente.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn color="grey" text @click="dialogEstingui = false">Annulla</v-btn>
          <v-btn
            color="orange darken-2"
            outlined
            :loading="loadingEstingui"
            @click="estinguiCredito"
            >Conferma</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>
  </span>
</template>

<script>
import allocazione_mixin from "../mixin/allocazione_mixin";
import ContabileOrdineRef from "./contabile_ordine_ref.vue";
export default {
  components: {
    ContabileOrdineRef,
  },
  mixins: [allocazione_mixin],
};
</script>
